// In-flight registry for manual IntelliSense schema refreshes, keyed by
// `(connectionId, database)`. One refresh can be started from several
// surfaces (Ctrl+Shift+R, the Command Palette, the Object Explorer's Refresh,
// the status-bar pill) and has to be VISIBLE on several others at once — the
// pill of every query tab on that database and the explorer's database /
// connection rows — so the flag lives here rather than in the component that
// happened to start it.
//
// Database names are case-folded in the key: SQL Server names are
// case-insensitive server-side and a tab's `database` can arrive differently
// cased than the explorer's listing (same rationale as `pairKey` in
// `introspectionTruncation.ts`).
//
// `begin` hands out an ownership token and `end` only removes the entry that
// token opened: a refetch that dies with a disconnect and settles AFTER a
// reconnect started a new refresh for the same pair must not close the
// successor's window. `end` also remembers the failure message (until the
// next `begin`) so a surface that did not start the refresh can still show
// the error instead of re-deriving a `ready`.

import { create } from 'zustand';

export type SchemaRefreshEntry = {
  readonly connectionId: string;
  readonly database: string;
  readonly startedAt: number;
  readonly token: number;
};

type SchemaRefreshState = {
  readonly inFlight: Readonly<Record<string, SchemaRefreshEntry>>;
  /** Failure message of the LAST refresh of a pair, cleared when a new one begins. */
  readonly lastError: Readonly<Record<string, string>>;
  /**
   * Per-pair change counter, advanced SYNCHRONOUSLY on every window change
   * (begin, end, clear). A caller that captured it when its run started can
   * tell at settle time whether anything else happened to the pair in
   * between — including changes React batched into a single render, which a
   * transition-watching effect never sees.
   */
  readonly generation: Readonly<Record<string, number>>;
  begin: (connectionId: string, database: string) => number;
  end: (connectionId: string, database: string, token: number, error?: string) => void;
  /**
   * Drops every entry of a connection — called on disconnect, connection
   * loss and deletion so a refresh that never settles (its refetch died with
   * the connection) cannot hold the spinner or block the successor session's
   * refresh.
   */
  clearConnection: (connectionId: string) => void;
};

export function schemaRefreshKey(connectionId: string, database: string): string {
  return `${connectionId}|${database.toLowerCase()}`;
}

let nextToken = 0;

function bumped(generation: Readonly<Record<string, number>>, keys: readonly string[]): Record<string, number> {
  const next = { ...generation };
  for (const key of keys) next[key] = (next[key] ?? 0) + 1;
  return next;
}

export const useSchemaRefreshStore = create<SchemaRefreshState>((set) => ({
  inFlight: {},
  lastError: {},
  generation: {},
  begin: (connectionId, database) => {
    const token = ++nextToken;
    const key = schemaRefreshKey(connectionId, database);
    set((state) => {
      const lastError = { ...state.lastError };
      delete lastError[key];
      return {
        inFlight: { ...state.inFlight, [key]: { connectionId, database, startedAt: Date.now(), token } },
        lastError,
        generation: bumped(state.generation, [key]),
      };
    });
    return token;
  },
  end: (connectionId, database, token, error) =>
    set((state) => {
      const key = schemaRefreshKey(connectionId, database);
      // A stale token (the entry was cleared on disconnect, or re-opened by
      // a successor) owns neither the window nor the error record — but its
      // settle is still a change to the pair, so the generation advances:
      // "own begin → clear → own stale end" must not count the same (+1) as
      // a clean "own begin → own end" to a caller fencing on it.
      if (state.inFlight[key]?.token !== token) return { generation: bumped(state.generation, [key]) };
      const next = { ...state.inFlight };
      delete next[key];
      return {
        inFlight: next,
        lastError: error ? { ...state.lastError, [key]: error } : state.lastError,
        generation: bumped(state.generation, [key]),
      };
    }),
  clearConnection: (connectionId) =>
    set((state) => {
      const keys = Object.keys(state.inFlight).filter((key) => state.inFlight[key].connectionId === connectionId);
      if (keys.length === 0) return state;
      const next = { ...state.inFlight };
      for (const key of keys) delete next[key];
      return { inFlight: next, generation: bumped(state.generation, keys) };
    }),
}));

/** The pair's change counter right now — see `generation` on the state. */
export function schemaRefreshGeneration(connectionId: string, database: string): number {
  return useSchemaRefreshStore.getState().generation[schemaRefreshKey(connectionId, database)] ?? 0;
}

/** Non-hook entry for the connection store's disconnect / connection-lost / delete paths. */
export function clearSchemaRefreshesForConnection(connectionId: string): void {
  useSchemaRefreshStore.getState().clearConnection(connectionId);
}

/** Non-hook read for imperative callers (dedupe before starting a refresh). */
export function isSchemaRefreshing(connectionId: string, database: string): boolean {
  return schemaRefreshKey(connectionId, database) in useSchemaRefreshStore.getState().inFlight;
}

/** The failure message of the last refresh of the pair, if it failed. */
export function lastSchemaRefreshError(connectionId: string, database: string): string | undefined {
  return useSchemaRefreshStore.getState().lastError[schemaRefreshKey(connectionId, database)];
}

/** True while a refresh for exactly this `(connectionId, database)` runs. */
export function useIsSchemaRefreshing(connectionId?: string, database?: string): boolean {
  return useSchemaRefreshStore((state) =>
    connectionId && database ? schemaRefreshKey(connectionId, database) in state.inFlight : false,
  );
}

/** True while a refresh for ANY database on `connectionId` runs. */
export function useIsConnectionSchemaRefreshing(connectionId?: string): boolean {
  return useSchemaRefreshStore((state) =>
    connectionId
      ? Object.values(state.inFlight).some((entry) => entry.connectionId === connectionId)
      : false,
  );
}

/** Test-only: wipe every entry between cases. */
export function __resetSchemaRefreshStoreForTest(): void {
  useSchemaRefreshStore.setState({ inFlight: {}, lastError: {}, generation: {} });
}
